import {
  type AiPredictionRead,
  type Memory,
  type MemoryConstructionParams,
  type MemoryGroup,
  type MemoryLane,
  type MemoryRuleDb,
  type ToolConfig,
  ToolConfigSchema,
  type TrackerMemory,
} from '@timelyapp/integration-cloud-api-spec';
import { DefaultDemoMemorySettings } from '../memory-demo-data/types';
import { constructMemoryGroups } from '../memory-processors/utils.memories';

export const createMockGroupedMemory = (
  {
    baseDate = new Date('2023-08-07T00:00:00.000Z'),
    startOffsetMins = 0,
    durationMins = 10,
  }: { baseDate: Date; startOffsetMins: number; durationMins: number },
  overrides: Partial<Memory> = {},
  paramsOverrides: Partial<MemoryConstructionParams> = {}
): MemoryGroup => {
  const memory = createMockMemory({ baseDate, startOffsetMins, durationMins }, overrides);

  return createMockMemoryGroupContainer([memory], paramsOverrides)[0];
};
/**
 * Mock a MemoryGroup object with the given overrides.
 */
export const createMockMemory = (
  {
    baseDate = new Date('2023-08-07T00:00:00.000Z'),
    startOffsetMins = 0,
    durationMins = 10,
  }: { baseDate: Date; startOffsetMins: number; durationMins: number },
  overrides: Partial<Memory> = {}
): Memory => {
  const baseTimeInSeconds = baseDate.getTime() / 1000;
  const defaultMemory = {
    id: 1,
    color: 'rgba(111, 66, 193, 0.2)',
    title: 'Some Memory Title',
    description: null,
    icon_url: null,
    verb: null,
    started_at: new Date((baseTimeInSeconds + startOffsetMins * 60) * 1000).toISOString(),
    ended_at: new Date(
      (baseTimeInSeconds + (startOffsetMins + durationMins) * 60) * 1000
    ).toISOString(),
    updated_at: new Date((baseTimeInSeconds + startOffsetMins * 60) * 1000).toISOString(),
    total_seconds: durationMins * 60,
    url: 'http://github.com/Timely/Frontend',
    context: 'Timely/Frontend',
    ignored: false,
    raw: {},
    category:
      overrides.category === undefined
        ? {
            id: 'developer_tools',
            title: 'Developer Tools',
          }
        : overrides.category,
    tool:
      overrides.tool === undefined
        ? {
            id: 'github',
            title: 'GitHub',
            raw: 'Github',
          }
        : overrides.tool,
    window_tool:
      overrides.tool === undefined
        ? {
            id: 'chrome',
            title: 'Google Chrome',
            raw: 'Google Chrome',
            icon_url: null,
          }
        : overrides.window_tool!,
    from_timely: false,
    tracker_source: 'mac',
    ...overrides,
  } satisfies Memory;

  // console.log('overrides.tool', overrides.tool, 'finalValue', defaultMemory.tool);

  return defaultMemory;
};

export const createMockMemoryGroupContainer = (
  memories: Memory[] = [],
  paramsOverrides: Partial<MemoryConstructionParams> = {}
): MemoryGroup[] => {
  // Create default params and apply overrides
  const params: MemoryConstructionParams = createMockMemoryQuery(paramsOverrides);

  // If no memories are passed in, create a default one
  if (memories.length === 0) {
    memories = [createMockMemory({ baseDate: new Date(), startOffsetMins: 0, durationMins: 10 })];
  }

  return constructMemoryGroups(memories, params);
};

export const createMockMemoryQuery = (
  overrides: Partial<MemoryConstructionParams> = {}
): MemoryConstructionParams => ({
  constructLanesOn: overrides.constructLanesOn || 'tool',
  toolConfigsMap: overrides.toolConfigsMap || {},
  groupMemoriesBy: overrides.groupMemoriesBy || 'tool',
  minMinuteGapBetweenMemories: overrides.minMinuteGapBetweenMemories || 8,
  minMinutesForDedicatedLane: overrides.minMinutesForDedicatedLane || 30,
  minutesPerBlock: overrides.minutesPerBlock || 30,
  pxPerMinute: overrides.pxPerMinute || 2,
  minimumDurationMinutes: overrides.minimumDurationMinutes || 1,
  minimumGroupDurationMinutes: overrides.minimumGroupDurationMinutes || 1,
  mergeWithinMinutes: overrides.mergeWithinMinutes || 8,
  startDate: overrides.startDate || new Date('2023-08-07T00:00:01.000Z').toISOString(),
  endDate: overrides.endDate || new Date('2023-08-07T23:59:59.000Z').toISOString(),
  showIgnored: overrides.showIgnored || false,
  compact: overrides.compact || false,
  showOtherMemoriesLane: overrides.showOtherMemoriesLane || true,
  namingStrategy: overrides.namingStrategy || 'tool',
  minPxPerMinute: overrides.minPxPerMinute || 2,
  demoSettings: overrides.demoSettings || DefaultDemoMemorySettings,
});

export const createMockTrackerMemory = (overrides: Partial<TrackerMemory> = {}): TrackerMemory => ({
  started_at: new Date('2023-08-07T00:10:00.000Z').toISOString(),
  ended_at: new Date('2023-08-07T00:11:00.000Z').toISOString(),

  title: 'Some Memory Title',
  ...overrides,
});

export const createMockMemoryRuleDb = (overrides: Partial<MemoryRuleDb> = {}): MemoryRuleDb => ({
  id: 1,
  type: 'ignore',
  matcher: 'equals',
  field: 'title',
  rewrite_to: null,
  value: '',
  ...overrides,
});

export const createMockMemoryLane = (overrides: Partial<MemoryLane> = {}): MemoryLane => ({
  type: overrides.type || 'time',
  title: overrides.title || 'Time Lane',
  total_seconds: overrides.total_seconds || 0,
  sub_lanes: overrides.sub_lanes || [],
  sticky: overrides.sticky || false,
  compact: overrides.compact || false,
  ignored: overrides.ignored || false,
  // Dedicated lane is automatically set to true if sticky is true at db-level
  dedicated_lane: overrides.dedicated_lane || overrides.sticky || false,
});

export const createMockAiPrediction = (
  overrides: Partial<AiPredictionRead> = {}
): AiPredictionRead => ({
  id: overrides.id || '123e4567-e89b-12d3-a456-426614174000',
  created_at: overrides.created_at || new Date('2023-08-07T00:10:00.000Z').toISOString(),
  user_id: overrides.user_id || '123e4567-e89b-12d3-a456-426614174000',
  started_at: overrides.started_at || new Date('2023-08-07T00:10:00.000Z').toISOString(),
  ended_at: overrides.ended_at || new Date('2023-08-07T00:11:00.000Z').toISOString(),
  timely_account_id: overrides.timely_account_id || 1,
  timely_user_id: overrides.timely_user_id || 1,
  flagged_as: null,
  origin_prediction_id: null,
  ...overrides,
  inferred: {
    timely_project_id: 1,
    note: null,
    valid_as_hour: true,
    ...(overrides.inferred || {}),
  },
});

/**
 * Creates a mock ToolConfig object with sensible defaults.
 *
 * @param toolId - The tool ID (e.g., 'github', 'slack')
 * @param overrides - Optional overrides for the ToolConfig properties
 * @returns A ToolConfig object
 */
export const mockToolConfig = (
  toolId: string,
  overrides: Partial<Omit<ToolConfig, 'preferences'>> &
    Partial<{
      preferences: Partial<ToolConfig['preferences']>;
    }> = {}
): ToolConfig =>
  ToolConfigSchema.parse({
    tool_id: toolId,
    tool_raw: null,
    ...overrides,
    preferences: {
      group_by: 'default',
      ignored: false,
      continuity: 'default',
      noise_cancellation: 'default',
      naming_strategy: 'default',
      compact: false,
      dedicated_lane: false,
      sticky: false,
      starred: false,
      ...overrides.preferences,
    },
  });
